import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import './BookingModal.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Context ──────────────────────────────────────────────────────────────────
const BookingContext = createContext();
export const useBooking = () => useContext(BookingContext);

// ─── Constants ────────────────────────────────────────────────────────────────
const SERVICES = ['Whitening', 'Dentures', 'Implants', 'Root Canal'];
const DEPARTMENTS = ['Cosmetic', 'General', 'Orthodontics', 'Surgery'];
const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatRefId(id) {
  if (!id) return '';
  // Convert raw MongoDB ObjectId (24 hex) → e.g. "DENT-A054F3"
  const code = id.slice(-6).toUpperCase();
  return `DENT-${code}`;
}

function fmt12(time24) {
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function groupByPeriod(slots) {
  return {
    morning:   slots.filter(s => s.start_time < '12:00'),
    afternoon: slots.filter(s => s.start_time >= '12:00' && s.start_time < '17:00'),
    evening:   slots.filter(s => s.start_time >= '17:00'),
  };
}

function getToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// ─── Stepper ──────────────────────────────────────────────────────────────────
const STEPS = [
  { n: 1, label: 'Slot' },
  { n: 2, label: 'Details' },
  { n: 3, label: 'Done' },
];

function Stepper({ step }) {
  return (
    <div className="bm-stepper">
      {STEPS.map(({ n, label }, i) => (
        <React.Fragment key={n}>
          <div className={`bm-step ${step === n ? 'active' : step > n ? 'done' : ''}`}>
            <div className="bm-step-circle">
              {step > n ? '✓' : n}
            </div>
            <span className="bm-step-label">{label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`bm-step-line ${step > n ? 'done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── SlotGroup ────────────────────────────────────────────────────────────────
function SlotGroup({ title, slots, selectedSlot, onSelect }) {
  if (!slots.length) return null;
  return (
    <div className="bm-slot-group">
      <h6 className="bm-group-title">
        <span className="bm-group-icon">⏰</span>
        {title} <span className="bm-group-count">({slots.length} slots)</span>
      </h6>
      <div className="bm-slot-grid">
        {slots.map(slot => {
          const slotId = slot._id || slot.id;
          const selectedId = selectedSlot ? (selectedSlot._id || selectedSlot.id) : null;
          return (
          <button
            key={slotId}
            className={`bm-slot-chip ${selectedId && selectedId === slotId ? 'selected' : ''}`}
            onClick={() => onSelect(slot)}
          >
            {fmt12(slot.start_time)}
          </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── BookingModal ─────────────────────────────────────────────────────────────
function BookingModal({ isOpen, onClose }) {
  const today = useMemo(() => getToday(), []);

  const initDate = useCallback(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d;
  }, [today]);

  const dateStrip = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      return d;
    }), [today]);

  // ── state ──
  const [step, setStep] = useState(1);
  const [service, setService] = useState('Whitening');
  const [department, setDepartment] = useState('Cosmetic');
  const [selectedDate, setSelectedDate] = useState(initDate);
  const [slots, setSlots] = useState([]);
  const [slotCounts, setSlotCounts] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false); // only for initial empty state
  const [selectedSlot, setSelectedSlot] = useState(null);
  const slotsAreaRef = useRef(null); // used to lock height during date switches
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [appointmentId, setAppointmentId] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);

  // OTP cooldown countdown
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  const handleSendOtp = async () => {
    setOtpError('');
    setOtpSending(true);
    try {
      const res = await fetch(`${API_URL}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: patientPhone, patient_name: patientName || 'Patient' }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Failed to send OTP. Please try again.');
      }
      setOtpSent(true);
      setOtpCode('');
      setOtpCooldown(30);
    } catch (err) {
      setOtpError(err.message === 'Failed to fetch'
        ? 'Unable to connect. Please check your internet.'
        : err.message);
    } finally {
      setOtpSending(false);
    }
  };

  // Lock body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Fetch slots for selected date + service
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const fetchSlots = async () => {
      setSelectedSlot(null);
      const isFirstLoad = slots.length === 0;
      if (isFirstLoad) {
        setLoadingSlots(true);
      } else {
        // Lock the slot area height to prevent layout shift during swap
        const el = slotsAreaRef.current;
        if (el) el.style.minHeight = `${el.offsetHeight}px`;
      }
      try {
        const res = await fetch(`${API_URL}/api/slots/?date=${toISODate(selectedDate)}`);
        const data = await res.json();
        if (!cancelled) {
          setSlots(Array.isArray(data) ? data.filter(s => s.is_available !== false && s.service === service) : []);
        }
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) {
          setLoadingSlots(false);
          // Unlock height after new slots paint
          requestAnimationFrame(() => {
            if (slotsAreaRef.current) slotsAreaRef.current.style.minHeight = '';
          });
        }
      }
    };
    fetchSlots();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, service, isOpen]);

  // Pre-fetch slot counts for all 7 days
  useEffect(() => {
    if (!isOpen) return;
    const fetchCounts = async () => {
      const entries = await Promise.all(
        dateStrip.map(async (d) => {
          const iso = toISODate(d);
          try {
            const res = await fetch(`${API_URL}/api/slots/?date=${iso}`);
            const data = await res.json();
            const count = Array.isArray(data) ? data.filter(s => s.is_available !== false && s.service === service).length : 0;
            return [iso, count];
          } catch {
            return [iso, 0];
          }
        })
      );
      setSlotCounts(Object.fromEntries(entries));
    };
    fetchCounts();
  }, [service, isOpen, dateStrip]);

  const handleReset = useCallback(() => {
    setStep(1);
    setService('Whitening');
    setDepartment('Cosmetic');
    setSelectedDate(initDate());
    setSlots([]);
    setSlotCounts({});
    setSelectedSlot(null);
    setPatientName('');
    setPatientEmail('');
    setPatientPhone('');
    setMessage('');
    setError('');
    setAppointmentId('');
    setOtpSent(false);
    setOtpCode('');
    setOtpSending(false);
    setOtpError('');
    setOtpCooldown(0);
  }, [initDate]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot_id: selectedSlot.id || selectedSlot._id,
          patient_name: patientName,
          patient_email: patientEmail,
          patient_phone: patientPhone,
          service,
          department,
          message,
          otp_code: otpCode,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail.map(e => e.msg || String(e)).join('; ')
          : (typeof detail === 'string' ? detail : 'Booking failed. Please try again.');
        throw new Error(msg);
      }
      setAppointmentId(data.id || data._id || '');
      setStep(3);
    } catch (err) {
      const msg = err.message === 'Failed to fetch'
        ? 'Unable to connect. Please check your internet and try again.'
        : err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const grouped = groupByPeriod(slots);

  return (
    <div className="bm-overlay" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="bm-modal">

        {/* ── Header ── */}
        <div className="bm-header">
          <div className="bm-header-info">
            <div className="bm-clinic-icon">🦷</div>
            <div>
              <h3 className="bm-clinic-name">Dentalist Clinic</h3>
              <p className="bm-clinic-sub">Book your appointment</p>
            </div>
          </div>
          <button className="bm-close-btn" onClick={handleClose} aria-label="Close">×</button>
        </div>

        {/* ── Stepper ── */}
        <Stepper step={step} />

        {/* ── Step 1: Select Slot ── */}
        {step === 1 && (
          <>
            <div className="bm-body">
              <h5 className="bm-section-title">Available Slots</h5>

              {/* Service + Department */}
              <div className="bm-selectors">
                <select
                  className="bm-select"
                  value={service}
                  onChange={e => { setService(e.target.value); setSelectedSlot(null); }}
                >
                  {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  className="bm-select"
                  value={department}
                  onChange={e => setDepartment(e.target.value)}
                >
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              {/* Date strip */}
              <div className="bm-date-strip-header">
                {(() => {
                  const first = dateStrip[0];
                  const last  = dateStrip[dateStrip.length - 1];
                  const sameMonth = first.getMonth() === last.getMonth();
                  return sameMonth
                    ? `${MONTH_NAMES[first.getMonth()]} ${first.getFullYear()}`
                    : `${MONTH_NAMES[first.getMonth()]} – ${MONTH_NAMES[last.getMonth()]} ${last.getFullYear()}`;
                })()}
              </div>
              <div className="bm-date-strip">
                {dateStrip.map(d => {
                  const iso = toISODate(d);
                  const count = slotCounts[iso];
                  const isSelected = iso === toISODate(selectedDate);
                  return (
                    <button
                      key={iso}
                      className={`bm-day-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => { setSelectedDate(new Date(d)); setSelectedSlot(null); }}
                    >
                      <span className="bm-day-name">{DAY_NAMES[d.getDay()]}</span>
                      <span className="bm-day-num">{d.getDate()}</span>
                      <span className={`bm-day-count ${count === 0 ? 'no-slot' : count > 0 ? 'has-slot' : ''}`}>
                        {count === undefined ? '…' : count === 0 ? 'No slot' : `${count} slots`}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Slot area */}
              <div className="bm-slots-area" ref={slotsAreaRef}>
                {loadingSlots ? (
                  <div className="bm-loading">
                    <div className="bm-spinner" />
                    <span>Loading slots…</span>
                  </div>
                ) : slots.length === 0 ? (
                  <div className="bm-no-slots">
                    <div className="bm-no-slots-icon">📅</div>
                    <p>No slots available</p>
                    <small>No appointment slots for the selected date &amp; service</small>
                  </div>
                ) : (
                  <>
                    <SlotGroup title="Morning" slots={grouped.morning} selectedSlot={selectedSlot} onSelect={setSelectedSlot} />
                    <SlotGroup title="Afternoon" slots={grouped.afternoon} selectedSlot={selectedSlot} onSelect={setSelectedSlot} />
                    <SlotGroup title="Evening" slots={grouped.evening} selectedSlot={selectedSlot} onSelect={setSelectedSlot} />
                  </>
                )}
              </div>
            </div>

            {/* Footer — outside bm-body so it never shifts */}
            <div className="bm-footer">
              <button
                className="bm-btn-primary"
                disabled={!selectedSlot}
                onClick={() => setStep(2)}
              >
                Continue →
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Patient Details ── */}
        {step === 2 && (
          <>
            <div className="bm-body">
              <h5 className="bm-section-title">Patient Details</h5>

              {/* Booking summary */}
              {selectedSlot && (
                <div className="bm-summary-bar">
                  <span>📅 {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}</span>
                  <span>⏰ {fmt12(selectedSlot.start_time)} – {fmt12(selectedSlot.end_time)}</span>
                  <span>👨‍⚕️ {selectedSlot.doctor_name}</span>
                </div>
              )}

              <div className="bm-chips">
                <span className="bm-chip">{service}</span>
                <span className="bm-chip">{department}</span>
              </div>

              {/* form id used to link submit button in the footer below */}
              <form id="bm-booking-form" onSubmit={handleSubmit}>
                <div className="bm-field">
                  <label>Full Name <span className="bm-required">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter patient's full name"
                    value={patientName}
                    onChange={e => setPatientName(e.target.value)}
                    required
                    minLength={2}
                  />
                </div>
                <div className="bm-field">
                  <label>Email Address <span className="bm-required">*</span></label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={patientEmail}
                    onChange={e => setPatientEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="bm-field">
                  <label>Phone Number <span className="bm-required">*</span></label>
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={patientPhone}
                    onChange={e => { setPatientPhone(e.target.value); setOtpSent(false); setOtpCode(''); setOtpError(''); }}
                    required
                  />
                </div>

                {/* OTP Section — shown when phone has 10+ digits */}
                {patientPhone.replace(/\D/g, '').length >= 10 && (
                  <div className="bm-otp-section">
                    <div className="bm-otp-send-row">
                      <button
                        type="button"
                        className="bm-otp-send-btn"
                        onClick={handleSendOtp}
                        disabled={otpSending || otpCooldown > 0}
                      >
                        {otpSending ? 'Sending…' : otpSent ? (otpCooldown > 0 ? `Resend OTP (${otpCooldown}s)` : 'Resend OTP') : 'Send OTP'}
                      </button>
                      {otpSent && (
                        <span className="bm-otp-status">OTP sent to {patientPhone}</span>
                      )}
                    </div>
                    {otpError && <div className="bm-otp-error">{otpError}</div>}
                    {otpSent && (
                      <input
                        type="text"
                        className="bm-otp-input"
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        value={otpCode}
                        onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                      />
                    )}
                  </div>
                )}
                <div className="bm-field">
                  <label>Message <span className="bm-optional">(Optional)</span></label>
                  <textarea
                    placeholder="Describe your concern or reason for visit"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={3}
                  />
                </div>

                {error && <div className="bm-error">{error}</div>}
              </form>
            </div>

            {/* Footer — outside bm-body so it never shifts */}
            <div className="bm-footer">
              <button type="button" className="bm-btn-back" onClick={() => setStep(1)}>
                ← Back
              </button>
              {/* form attribute links this button to the form above even though it's outside */}
              <button
                type="submit"
                form="bm-booking-form"
                className="bm-btn-primary"
                disabled={submitting || (patientPhone.replace(/\D/g, '').length >= 10 && (!otpSent || otpCode.length < 6))}
              >
                {submitting ? 'Booking…' : 'Confirm Booking →'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Success ── */}
        {step === 3 && (
          <>
            <div className="bm-body bm-step3">
              <div className="bm-success-icon">✓</div>
              <h3 className="bm-success-title">Appointment Booked!</h3>
              <p className="bm-success-msg">Your appointment has been successfully scheduled.</p>

              {selectedSlot && (
                <div className="bm-confirm-details">
                  <div className="bm-confirm-row">
                    <span>📅 Date</span>
                    <strong>{MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}</strong>
                  </div>
                  <div className="bm-confirm-row">
                    <span>⏰ Time</span>
                    <strong>{fmt12(selectedSlot.start_time)} – {fmt12(selectedSlot.end_time)}</strong>
                  </div>
                  <div className="bm-confirm-row">
                    <span>👨‍⚕️ Doctor</span>
                    <strong>{selectedSlot.doctor_name}</strong>
                  </div>
                  <div className="bm-confirm-row">
                    <span>🦷 Service</span>
                    <strong>{service}</strong>
                  </div>
                  {appointmentId && (
                    <div className="bm-confirm-row bm-ref-id">
                      <span>Booking Ref</span>
                      <strong>{formatRefId(appointmentId)}</strong>
                    </div>
                  )}
                </div>
              )}

              <p className="bm-email-note">
                A confirmation email has been sent to <strong>{patientEmail}</strong>.
              </p>
            </div>

            {/* Footer — outside bm-body so it never shifts */}
            <div className="bm-footer">
              <button className="bm-btn-primary" onClick={handleClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Provider (wraps the whole app) ──────────────────────────────────────────
export function BookingProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <BookingContext.Provider value={{ openBooking: () => setIsOpen(true) }}>
      {children}
      <BookingModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </BookingContext.Provider>
  );
}

export default BookingModal;
